import { useState, useCallback, useEffect } from "react";
import { useMutation } from "urql";
import * as z from "zod";
import { useNavigate } from "react-router-dom";
import Modal from "../../../../ui/Modal/Modal";
import { graphql } from "../../../../__generated__/gql";
import urls from "../../../../routes";
import SelectMethod from "./SelectMethod/SelectMethod";
import AddKeywords from "./AddKeywords/AddKeywords";
import ConfirmKeywords from "./ConfirmKeywords/ConfirmKeywords";
import styles from "./NewArticleModal.module.css";
import { useToast } from "../../../../ui/Toast/ToastContext";

const GENERATE_KEYWORD_ARTICLES_MUTATION = graphql(/* GraphQL */ `
  mutation generateKeywordArticles($input: GenerateKeywordArticlesInput!) {
    generateKeywordArticles(input: $input) {
      ... on GenerateArticlesResult {
        count
      }
      ... on InsufficientCreditsError {
        __typename
        message
      }
    }
  }
`);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const schema = z.object({
  status: z.enum(["draft", "generate"]),
  keywords: z
    .object({ keyword: z.string().nonempty("Required") })
    .array()
    .min(1, { message: "Required" }),
});

type NewArticleFormData = z.infer<typeof schema>;

const defaultFormData: NewArticleFormData = {
  status: "generate",
  keywords: [{ keyword: "" }],
};

function NewArticleModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [step, setStep] = useState<
    "SelectMethod" | "AddKeywords" | "ConfirmKeywords" | null
  >(isOpen ? "SelectMethod" : null);
  const [formData, setFormData] = useState<NewArticleFormData>(defaultFormData);

  const [, generateKeywordArticles] = useMutation(
    GENERATE_KEYWORD_ARTICLES_MUTATION,
  );

  const { keywords } = formData;

  const onSubmit = useCallback(
    async (data: NewArticleFormData) => {
      const generateArticlesResponse = await generateKeywordArticles({
        input: {
          articles: data.keywords.map((keyword) => ({
            keyword: keyword.keyword,
            draft: data.status === "draft",
          })),
        },
      });

      if (
        generateArticlesResponse.data?.generateKeywordArticles.__typename ===
        "InsufficientCreditsError"
      ) {
        showToast({
          title: generateArticlesResponse.data.generateKeywordArticles.message,
          variant: "error",
        });
      }
      onClose();
    },
    [generateKeywordArticles, onClose, showToast],
  );

  useEffect(() => {
    if (!isOpen) {
      setStep(null);
    } else {
      setStep("SelectMethod");
    }
  }, [isOpen]);

  return (
    <>
      <Modal
        isOpen={step === "SelectMethod"}
        onClose={onClose}
        subtitle="Select a method below"
        title="Add a new Article"
      >
        <SelectMethod
          // onSelectKeywordList={() => {
          //   setStep("AddKeywords");
          // }}
          onSelectNewKeywords={() => {
            setStep("AddKeywords");
          }}
          onSelectPattern={() => navigate(urls.newPattern)}
        />
      </Modal>
      <Modal
        classNames={{ body: styles.addKeywordsModalBody }}
        isOpen={step === "AddKeywords"}
        onClose={onClose}
        subtitle="Add one or more keywords to start writing articles. Each keyword will create a new article."
        title="Add new keywords"
      >
        <AddKeywords
          defaultValues={{ keywords: formData.keywords }}
          onBack={() => {
            setStep("SelectMethod");
          }}
          onSubmit={(data) => {
            setFormData((previousFormData) => ({
              ...previousFormData,
              ...data,
            }));
            setStep("ConfirmKeywords");
          }}
        />
      </Modal>
      <Modal
        isOpen={step === "ConfirmKeywords"}
        onClose={onClose}
        size="medium"
        title={`Add ${keywords.length} new ${
          keywords.length === 1 ? "keyword" : "keywords"
        }?`}
      >
        <ConfirmKeywords
          defaultValues={formData}
          onBack={() => {
            setStep("AddKeywords");
          }}
          onSubmit={async (data) => {
            await onSubmit(data);
            setFormData(defaultFormData);
            onClose();
          }}
        />
      </Modal>
    </>
  );
}

export default NewArticleModal;
